<template>
  <div v-bind="$attrs">
    <label>
      {{ label }}
      <component
        :is="getComponentName"
        :id="id"
        ref="select"
        v-model="value"
        v-bind="getComponentProps"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :autofocus="autofocus"
        :items="items"
        :error="!!error"
        :error-messages="error ? [error] : undefined"
        :data-testid="getComponentTestId"
        :bg-color="readonly ? 'background' : 'surface'"
        :rules="rules"
        :clearable="clearable"
        :prefix="prefix"
        color="success"
        variant="solo"
        hide-details="auto"
        item-title="title"
        item-value="value"
        density="compact"
        class="mb-1"
        validate-on="input"
        :chips="getComponentProps.multiple"
        @click.stop
      >
        <template v-if="getComponentProps.multiple" #chip="{ item, props }">
          <v-chip
            v-bind="props"
            closable
            outlined
            close-icon="close"
            data-testid="so-select-chip"
          >
            {{ item.title }}
          </v-chip>
        </template>
        <template v-if="$slots.item" #item="slotProps">
          <slot name="item" v-bind="slotProps" />
        </template>
        <template v-if="$slots.selection" #selection="slotProps">
          <slot name="selection" v-bind="slotProps" />
        </template>
      </component>
    </label>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { ValidationRule } from "@/types/ValidationRule"
  import { VSelect, VAutocomplete, VCombobox } from "vuetify/components"
  import { SoSelectItem, SoSelectValue } from "@/types/components/SoSelect"

  type SoSelectTypeProps = {
    autocomplete?: boolean
    multiple?: boolean
    clearable?: boolean
    combobox?: boolean
  }

  interface SoSelectTypes {
    readonly [char: string]: SoSelectTypeProps
  }

  const TYPES: SoSelectTypes = {
    default: {
      autocomplete: true,
    },
    simple: {
      autocomplete: false,
    },
    multiple: {
      autocomplete: true,
      multiple: true,
      clearable: true,
    },
    combobox: {
      combobox: true,
    },
  }

  export default defineComponent({
    components: {
      VSelect,
      VAutocomplete,
      VCombobox,
    },
    inheritAttrs: false,
    props: {
      modelValue: {
        type: [
          String,
          Number,
          Array,
          Object,
          Boolean,
        ] as PropType<SoSelectValue>,
        default: null,
      },
      label: {
        type: String,
        default: null,
      },
      items: {
        type: Array as PropType<Array<SoSelectItem>>,
        default: () => [],
      },
      type: {
        type: String,
        default: "default",
        validator: (val: string) => val in TYPES,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      readonly: {
        type: Boolean,
        default: false,
      },
      autofocus: {
        type: Boolean,
        default: false,
      },
      error: {
        type: String,
        default: undefined,
      },
      placeholder: {
        type: String,
        default: undefined,
      },
      rules: {
        type: Array as PropType<ValidationRule[]>,
        default: [] as ValidationRule[],
      },
      clearable: {
        type: Boolean,
        default: false,
      },
      prefix: {
        type: String,
        default: null,
      },
      id: {
        type: String,
        default: undefined,
      },
    },
    emits: ["update:modelValue"],
    computed: {
      value: {
        get(): SoSelectValue {
          return this.modelValue
        },
        set(val: SoSelectValue) {
          this.$emit("update:modelValue", val)
        },
      },
      getProps() {
        return TYPES[this.type]
      },
      getComponentProps() {
        const { autocomplete, combobox, ...componentProps } = this.getProps
        return componentProps
      },
      getComponentName(): string {
        if (this.getProps.autocomplete) {
          return "v-autocomplete"
        } else if (this.getProps.combobox) {
          return "v-combobox"
        } else return "v-select"
      },
      getComponentTestId(): string {
        if (this.getProps.autocomplete) {
          return "so-autocomplete"
        } else if (this.getProps.combobox) {
          return "so-combobox"
        } else return "so-select"
      },
    },
  })
</script>

<style scoped>
  :deep(.v-field) {
    min-height: 48px;
    min-width: 200px;
    border-radius: var(--theme-radius);
    transition: border 250ms;
  }
  :deep(.v-field__input) {
    top: 2px;
    display: flex;
    align-content: center;
  }
  :deep(.v-field:hover) {
    border: 1px solid rgb(var(--v-theme-on-surface));
  }
  :deep(.v-field--focused) {
    outline: var(--theme-outline);
  }
  :deep(.v-field--error) {
    border: 1px solid rgb(var(--v-theme-error)) !important;
  }
  :deep(.v-field--variant-solo) {
    box-shadow: none !important;
    border: 1px solid rgb(var(--v-theme-border));
  }
  :deep(.v-autocomplete__selection-text) {
    white-space: nowrap;
  }
  :deep(.v-messages__message) {
    line-height: normal;
  }
  :deep(.v-text-field__prefix) {
    align-self: center;
  }
  :deep(.v-chip__underlay) {
    background: rgb(var(--v-theme-surface-dim));
    opacity: 1;
  }
  :deep(.v-chip__content) {
    z-index: 1;
  }
</style>
